/**
 * Esoteria music addressing — the real model, read out of the retail binary.
 *
 * There is no "gameplay state" dimension in the original engine. Music is
 * addressed by exactly two numbers:
 *
 *   (level 0..9, intensity 0..2)  ->  a row of music container entry ids
 *
 * Provenance (all of it verified against ESOTERIA.EXE, see
 * `tools/music-decode/exe_tables.mjs` and `wiki/music-system-reverse-engineering.md`):
 *
 *   - `0x50a780` SONG GRID: 9 songs x 3 intensity rows x 10 x u16. The row
 *     index *is* the intensity. Values are music entry ids. `0xffff` marks an
 *     empty slot and was dropped when `assets/data/songs.json` was generated.
 *   - `0x50a0b8` / `0x50a0e0` LEVEL -> SONG: two parallel 10 x u32 tables
 *     ("a" and "b") indexed by level. The engine picks between the two
 *     probabilistically via a float LCG (`0x4d25ba`): 70% Table A, 30% Table B.
 *     All 141 retail sectors in `AREAS.E3` specify level index 2, which maps to
 *     Song 0 under both tables (100% deterministic).
 *   - `0x52c450` per-entry kind table: in 300..699 even ids are kind 1
 *     ("intro") and odd ids are kind 3 ("continuation"). Each grid value is an
 *     intro whose continuation sits at `id + 1`, and the original plays the
 *     two gaplessly as one unit via `0x4d45a0(t, n, n + 1, -1, 0)`.
 *
 * Two properties of the data are deliberate and must not be "tidied up":
 *
 *   1. `entryIds` is in grid order, which is playback order, and is NOT
 *      sorted. Song 6 / intensity 1 genuinely plays 596 before 594.
 *   2. `missingEntryIds` lists entries the grid references but that do not
 *      exist in the retail container (e.g. 706 in song 3 / intensity 0 — note
 *      it is outside the 300..699 music range entirely). They are skipped at
 *      runtime, never treated as an error.
 *
 * There is no weighting. The old version of this file carried per-segment
 * `weight` values invented to bias a random draw; the engine does no such
 * thing — a row is an ordered sequence that plays through in order.
 */
import songsJson from '../../assets/data/songs.json'

/** Intensity row index into a song. 0..2, straight out of the song grid. */
export type MusicIntensity = 0 | 1 | 2

/**
 * Level index into the level->song tables, 0..9.
 *
 * In retail AREAS.E3, all 141 sectors serialize level index 2, which maps to
 * Song 0 (entries 300..340) in both Table A and Table B. Indices 0, 1, 3..9
 * exist in engine capacity but are not used by shipped retail sectors.
 */
export type MusicLevelId = number

/** Which of the two level->song tables to read. Chosen probabilistically via float LCG. */
export type LevelSongTable = 'a' | 'b'

/** Song index, 0..8. */
export type MusicSongId = number

/**
 * Human-readable names for the three intensity rows.
 *
 * !! INFERRED, NOT RECOVERED !!
 *
 * The binary has no names for these rows. All that is established is the
 * ordering: the engine walks a ladder of float thresholds against a danger
 * metric and lands on row 0, 1 or 2, with 0 the least dangerous. The words
 * below are our own reading of that ordering and are for HUD/debug display
 * only — do not treat them as recovered fact, and do not branch behaviour on
 * them.
 */
export const INTENSITY_LABELS: Readonly<Record<MusicIntensity, 'calm' | 'alert' | 'combat'>> = {
  0: 'calm',
  1: 'alert',
  2: 'combat',
} as const

/** Number of songs in the grid at `0x50a780`. */
export const SONG_COUNT = 9
/** Number of intensity rows per song. */
export const INTENSITY_COUNT = 3
/** Number of entries in each level->song table. */
export const LEVEL_COUNT = 10

/** Every valid intensity, in ladder order. */
export const MUSIC_INTENSITIES: readonly MusicIntensity[] = [0, 1, 2]

/** Every valid level->song table key, in the order the engine's random pick sees them. */
export const LEVEL_SONG_TABLES: readonly LevelSongTable[] = ['a', 'b']

/** The intensity playback starts at. Lowest rung of the ladder. */
export const DEFAULT_INTENSITY: MusicIntensity = 0

/** The level playback starts at. Level 2 is the universal retail campaign level. */
export const DEFAULT_LEVEL: MusicLevelId = 2

/** Inclusive bounds of the music id range in the container's kind table. */
export const MUSIC_ENTRY_ID_MIN = 300
export const MUSIC_ENTRY_ID_MAX = 699

/** A level that reaches a song row, and via which of the two tables. */
export interface SongLevelRef {
  level: MusicLevelId
  tables: LevelSongTable[]
}

/** One row of the song grid: a single (song, intensity) pair. */
export interface SongRow {
  songId: MusicSongId
  intensity: MusicIntensity
  /**
   * Music entry ids in grid order, which is playback order. Never sort this.
   * May include ids listed in `missingEntryIds`.
   */
  entryIds: number[]
  /** Subset of `entryIds` with no audio in the retail container. */
  missingEntryIds: number[]
  /** Levels that reach this song, and through which table(s). */
  levels: SongLevelRef[]
  /** Summed duration of the extracted audio for this row, in seconds. */
  totalDurationSec: number
  /** Provenance marker written by the generator (always `esoteria-exe`). */
  source: string
}

/**
 * An intro/continuation pair: one kind-1 entry plus the kind-3 entry at
 * `introId + 1`. The original plays the two gaplessly as a single unit.
 */
export interface MusicSegmentPair {
  introId: number
  continuationId: number
}

/** Narrowing guard for the raw JSON's plain `number` intensity. */
function isMusicIntensity(value: number): value is MusicIntensity {
  return value === 0 || value === 1 || value === 2
}

/**
 * The song grid as committed to `assets/data/songs.json`, in file order
 * (song-major, then intensity). 27 rows: 9 songs x 3 intensities.
 *
 * Rows are passed through unchanged apart from narrowing `intensity`; a row
 * with an out-of-range intensity would be generator corruption, so it is
 * dropped with a warning rather than silently mistyped.
 */
export const SONG_ROWS: readonly SongRow[] = (songsJson as SongRow[]).filter((row) => {
  if (isMusicIntensity(row.intensity)) return true
  console.warn(
    `songs.json: dropping song ${row.songId} row with out-of-range intensity ${row.intensity}`,
  )
  return false
})

/** Index of `SONG_ROWS` by `songId:intensity`, built once at module load. */
const ROW_INDEX: Map<string, SongRow> = new Map(
  SONG_ROWS.map((row) => [`${row.songId}:${row.intensity}`, row]),
)

/**
 * Level -> song, per table, reconstructed from the `levels` back-references
 * the generator wrote into each row. Both tables cover all 10 levels.
 */
const LEVEL_SONG_INDEX: Map<string, MusicSongId> = (() => {
  const index = new Map<string, MusicSongId>()
  for (const row of SONG_ROWS) {
    for (const ref of row.levels) {
      for (const table of ref.tables) {
        index.set(`${ref.level}:${table}`, row.songId)
      }
    }
  }
  return index
})()

/** True if `level` is a valid index into the level->song tables. */
export function isMusicLevelId(level: number): level is MusicLevelId {
  return Number.isInteger(level) && level >= 0 && level < LEVEL_COUNT
}

/** Clamps any number onto the intensity ladder. */
export function clampIntensity(value: number): MusicIntensity {
  if (!Number.isFinite(value)) return DEFAULT_INTENSITY
  const rounded = Math.round(value)
  if (rounded <= 0) return 0
  if (rounded >= INTENSITY_COUNT - 1) return 2
  return 1
}

/**
 * Resolve a level to its song through one of the two level->song tables.
 *
 * @returns the song id, or `null` for an out-of-range level.
 */
export function songForLevel(level: MusicLevelId, table: LevelSongTable): MusicSongId | null {
  const songId = LEVEL_SONG_INDEX.get(`${level}:${table}`)
  if (songId === undefined) {
    console.warn(`No song mapped for level ${level} in level->song table "${table}"`)
    return null
  }
  return songId
}

/**
 * Pick which level->song table to use.
 *
 * The authentic engine evaluates a float LCG (Numerical Recipes multiplier
 * 0x19660d, addend 0x3c6ef35f) and compares against 0.7 at 0x4d25ba:
 *   rand < 0.7  -> Table A (70% probability)
 *   rand >= 0.7 -> Table B (30% probability)
 *
 * @param rng - injectable uniform [0, 1) source, for deterministic tests.
 */
export function pickLevelSongTable(rng: () => number = Math.random): LevelSongTable {
  return rng() < 0.7 ? 'a' : 'b'
}

/** The raw grid row for a (song, intensity), or `undefined` if there is none. */
export function songRow(songId: MusicSongId, intensity: MusicIntensity): SongRow | undefined {
  return ROW_INDEX.get(`${songId}:${intensity}`)
}

/** Every level that can reach `songId`, through either table. */
export function levelsForSong(songId: MusicSongId): SongLevelRef[] {
  const row = SONG_ROWS.find((r) => r.songId === songId)
  return row ? row.levels : []
}

/**
 * The ordered intro/continuation pairs for a (song, intensity) row.
 *
 * Grid order is preserved exactly — the result is never sorted. Entries listed
 * in the row's `missingEntryIds` have no audio in the retail container and are
 * skipped with a warning rather than throwing, so a partially-extracted asset
 * set still plays.
 *
 * @returns `[]` for an unknown (song, intensity), so callers can degrade.
 */
export function segmentsFor(songId: MusicSongId, intensity: MusicIntensity): MusicSegmentPair[] {
  const row = songRow(songId, intensity)
  if (!row) {
    console.warn(`No song grid row for song ${songId} intensity ${intensity}`)
    return []
  }

  const missing = new Set(row.missingEntryIds)
  const pairs: MusicSegmentPair[] = []
  for (const introId of row.entryIds) {
    if (missing.has(introId)) {
      console.warn(
        `Skipping music entry ${introId} (song ${songId} intensity ${intensity}): ` +
          'referenced by the song grid but absent from the retail container',
      )
      continue
    }
    pairs.push({ introId, continuationId: introId + 1 })
  }
  return pairs
}

/**
 * Flatten a pair list into the container entry ids in playback order:
 * intro, continuation, intro, continuation, ...
 */
export function pairEntryIds(pairs: readonly MusicSegmentPair[]): number[] {
  const ids: number[] = []
  for (const pair of pairs) {
    ids.push(pair.introId, pair.continuationId)
  }
  return ids
}
